import { getUserInitials } from '@app/utils/helpers/getUserInitials';
import { UserEntity } from '@data/index';
import { googleLogout } from '@react-oauth/google';
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { ChevronsUpDown, LogOut, Settings, UserPlus } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/ui/popover';

import { clearSession } from '@app/constants/app/storage';
import { settingsPath } from '@app/constants/url';
import { useOrganizationId } from '@app/utils/hooks';
import { useStore } from '@data/storages';
import { useViewTransitionNav } from '@app/components/core/viewTransition';
import InviteColleaguesDialog from './InviteColleaguesDialog';

// Shared style for the profile-menu rows so every one of them looks identical.
const menuRowClass =
  'flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted';

interface ProfilePopoverProps {
  user: UserEntity;
  // Which side the popover opens toward. 'top' for the sidebar footer; the client header opens down.
  side?: 'top' | 'bottom';
  align?: 'start' | 'center' | 'end';
  // A client gets their colleagues here; what Settings holds for each is decided on that page.
  isEmployee?: boolean;
}

const ProfilePopover: React.FC<ProfilePopoverProps> = ({ user, side = 'top', align = 'start', isEmployee = true }) => {
  const { t } = useTranslation();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const transitionNavigate = useViewTransitionNav();
  const { pathname } = useLocation();
  const { organization } = useStore();
  const organizationId = useOrganizationId() ?? organization?.id;
  // The page lives under an organization, so someone in none has no Settings to open.
  const settingsUrl = organizationId ? settingsPath(organizationId) : undefined;
  // No nav item is lit on Settings, so this row carries the "you are here" instead.
  const isOnSettings = !!settingsUrl && pathname === settingsUrl;

  const hasName = Boolean(user.firstName || user.lastName);
  const fullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();

  const handleSignOutSuccess = () => {
    googleLogout();
    // The wipe is the session's, not the browser's — see `clearSession`.
    clearSession();
    window.location.href = '/';
  };

  const openSettings = (url: string) => () => {
    setPopoverOpen(false);
    transitionNavigate(url, 'fade');
  };

  // The popover has to go before the dialog does, or the dialog opens behind it.
  const openInvite = () => {
    setPopoverOpen(false);
    setInviteOpen(true);
  };

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger
          // rounded-xl, not the rows' rounded-md: the same corner in both places, or the eye reshapes it.
          className={cx(
            'flex w-full cursor-pointer items-center gap-2 rounded-xl border-none p-2 text-left outline-none transition-colors hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-sidebar-ring',
            isOnSettings ? 'bg-sidebar-accent' : 'bg-transparent',
          )}
          render={<button type="button" aria-label="Profile menu" />}
        >
          <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-neutral-400 text-sm font-semibold text-gray-50">
            {getUserInitials(user)}
          </div>
          <div className="grid min-w-0 flex-1 leading-tight">
            <span className="truncate text-sm font-medium text-sidebar-foreground">
              {hasName ? fullName : user.email}
            </span>
            {hasName && <span className="truncate text-xs text-sidebar-foreground/70">{user.email}</span>}
          </div>
          <ChevronsUpDown className="ml-auto size-4 shrink-0 text-sidebar-foreground/70" aria-hidden />
        </PopoverTrigger>
        {/* Sized to the trigger, the way the dropdown and select popups are: the menu belongs to the
            profile row it grew out of, and standing wider than it read as a panel that had come from
            somewhere else. */}
        <PopoverContent align={align} side={side} sideOffset={8} className="w-(--anchor-width) p-4">
          {/* Same avatar and type sizes as the trigger row, so the header still holds a full email
              address now that the popup is only as wide as that row. */}
          <div className="mb-2 flex w-full items-center gap-2">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-neutral-400 text-sm font-semibold text-gray-50">
              {getUserInitials(user)}
            </div>
            <div className="min-w-0">
              <p className="truncate font-medium text-foreground">{hasName ? fullName : user?.email}</p>
              {hasName && <p className="truncate text-xs text-neutral-600">{user?.email}</p>}
            </div>
          </div>
          <div className="mt-2 space-y-1 border-t border-border pt-2">
            {settingsUrl && (
              <button type="button" onClick={openSettings(settingsUrl)} className={menuRowClass}>
                <Settings className="size-4 shrink-0 text-muted-foreground" />
                {t('settings.title')}
              </button>
            )}
            {/* No GitHub row: this branch gives Settings the Phill bot's connections section, which is
                the thing that stopgap was waiting for, and it holds GitHub beside Slack and the calendar.
                A client has no such section — so their half of that row stays, named for what they come
                here to do. It opens on who already has access, which answers "is my colleague already on
                this?"; "People with access" put the word nobody was looking for on the only way in. */}
            {!isEmployee && (
              <button type="button" onClick={openInvite} className={menuRowClass}>
                <UserPlus className="size-4 shrink-0 text-muted-foreground" />
                {t('actions.inviteColleagues')}
              </button>
            )}
            <button type="button" onClick={handleSignOutSuccess} className={menuRowClass}>
              <LogOut className="size-4 shrink-0 text-muted-foreground" />
              {t('actions.signOut')}
            </button>
          </div>
        </PopoverContent>
      </Popover>
      {!isEmployee && <InviteColleaguesDialog open={inviteOpen} onOpenChange={setInviteOpen} user={user} />}
    </>
  );
};

export default ProfilePopover;
